import { useEffect, useMemo, useState } from 'react';
import { TableContextValue } from '../../context';
import { EnhancedTableProps, FilterOperator } from '../../types';
import {
  useColumnReorder,
  useColumnResize,
  useColumnVisibility,
  useDataProcessing,
  useExpansion,
  useFilters,
  useInlineEdit,
  useKeyboardNavigation,
  usePagination,
  useSelection,
  useServerDataManager,
  useSorting,
  useStatePersistence,
  useTableState,
} from '../';

/**
 * Main orchestrator hook that manages all table state and creates context value
 *
 * This hook consolidates all the individual table hooks and creates a unified
 * context value that can be shared across table components.
 *
 * @template T - The type of data rows in the table
 * @param props - Enhanced table properties
 * @returns Table context value with all state and handlers
 */
export function useTableOrchestrator<T extends Record<string, unknown>>(
  props: EnhancedTableProps<T>,
): TableContextValue<T> {
  const {
    columns,
    rows,
    getRowId: getRowIdProp,
    serverSide = false,
    serverDataFetcher,
    serverSideConfig,
    pagination: paginationProps,
    sorting: sortingProps,
    columnVisibility: initialColumnVisibility,
    onColumnVisibilityChange,
    selectedRows: initialSelectedRows,
    onSelect,
    onSelectAll: _onSelectAllProp,
    expandedRows: initialExpandedRows,
    onRowExpand,
    isLoading = false,
    error,
    filterConfigs = [],
    showFilters = false,
    showHeader = false,
    dense = false,
    stickyHeader = false,
    onSearch,
    inlineEdit,
    keyboardNavigation,
    statePersistence,
    bulkActions = [],
    initialColumnWidths = {},
    columnOrder: initialColumnOrder,
    onColumnResize,
    onColumnReorder,
    columnReorder = false,
  } = props;

  const getRowId =
    getRowIdProp ?? ((row: T) => (row as any)['id'] as string | number);
  const [searchValue, setSearchValue] = useState('');

  // Server-side data management
  const serverDataManager = useServerDataManager({
    fetcher:
      serverDataFetcher ||
      (() =>
        Promise.resolve({
          data: [],
          total: 0,
          page: 0,
          pageSize: 0,
          totalPages: 0,
        })),
    initialParams: serverSideConfig?.initialParams,
    debounceMs: serverSideConfig?.debounceMs,
    onError: serverSideConfig?.onError,
  });

  // Filter management
  const filterManager = useFilters({
    initialFilters: serverDataManager.queryParams.filters || [],
    onFiltersChange: serverSide ? serverDataManager.setFilters : undefined,
  });

  // Initialize state management
  const tableState = useTableState({
    isLoading: serverSide ? serverDataManager.isLoading || false : isLoading,
    error: serverSide ? serverDataManager.error : error,
    rows: serverSide ? serverDataManager.data || [] : rows,
  });

  // Initialize hooks
  const sorting = useSorting(sortingProps);
  const pagination = usePagination(paginationProps);
  const selection = useSelection(initialSelectedRows, onSelect);
  const expansion = useExpansion(initialExpandedRows, onRowExpand);
  const columnVisibility = useColumnVisibility(
    columns,
    initialColumnVisibility,
    onColumnVisibilityChange,
  );

  // Data processing - use server data or client-side processing
  const actualRows = serverSide ? serverDataManager.data || [] : rows;

  // Advanced feature hooks
  const columnResize = useColumnResize(initialColumnWidths, onColumnResize);
  const columnReorderHook = useColumnReorder(
    initialColumnOrder,
    onColumnReorder,
  );
  const inlineEditHook = useInlineEdit<T>(inlineEdit?.onCellEdit);

  const keyboardNavigationHook = useKeyboardNavigation(
    keyboardNavigation || { enabled: false },
    actualRows.length,
    columnVisibility.visibleColumns.length,
    () => {
      // Handle cell selection - do nothing to prevent Enter from selecting entire rows
    },
    (rowIndex: number, columnIndex: number) => {
      // Handle cell edit - start editing regardless of triggerMode for keyboard navigation
      const row = actualRows[rowIndex];
      const column = columnVisibility.visibleColumns[columnIndex];
      if (row && column && column.editable && inlineEditHook) {
        const rowId = getRowId(row);
        const currentValue = row[column.id];
        inlineEditHook.startEdit(rowId, column.id, currentValue);
      }
    },
  );

  const statePersistenceHook = useStatePersistence<T>(
    statePersistence || {
      key: 'table-state',
      storage: 'localStorage',
      fields: [],
    },
  );

  // Apply column ordering
  const orderedColumns = useMemo(() => {
    if (columnReorder) {
      return columnReorderHook.getOrderedColumns(
        columnVisibility.visibleColumns,
      );
    }
    return columnVisibility.visibleColumns;
  }, [columnReorder, columnReorderHook, columnVisibility.visibleColumns]);

  const { paginatedRows } = useDataProcessing({
    rows: tableState.shouldShowData ? (serverSide ? actualRows : rows) : [],
    searchValue: tableState.shouldShowData && !serverSide ? searchValue : '',
    sorting:
      tableState.shouldShowData && !serverSide ? sorting.sorting : undefined,
    pagination:
      tableState.shouldShowData && !serverSide
        ? pagination.pagination
        : undefined,
  });

  // Event handlers
  const handleSearch = (value: string) => {
    if (serverSide) {
      serverDataManager.setSearch(value);
    } else {
      setSearchValue(value);
    }
    onSearch?.(value);
  };

  const handleSort = (property: keyof T) => {
    if (serverSide) {
      const currentSort = serverDataManager.queryParams.sorting;
      const isAsc =
        currentSort?.field === String(property) &&
        currentSort?.direction === 'asc';
      const newDirection = isAsc ? 'desc' : 'asc';

      serverDataManager.setSorting({
        field: String(property),
        direction: newDirection,
      });
    } else {
      sorting.handleSort(property);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    if (serverSide && serverDataManager.queryParams.pagination) {
      serverDataManager.setPagination({
        ...serverDataManager.queryParams.pagination,
        page: newPage,
      });
    } else {
      pagination.onPageChange(event, newPage);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    if (serverSide && serverDataManager.queryParams.pagination) {
      serverDataManager.setPagination({
        page: 0,
        pageSize: parseInt(event.target.value, 10),
      });
    } else {
      pagination.onRowsPerPageChange(event);
    }
  };

  // State persistence effects - load state only once on mount
  useEffect(() => {
    if (statePersistence && statePersistence.fields.length > 0) {
      const savedState = statePersistenceHook.loadState();
      if (savedState) {
        // TODO: Actually apply the loaded state to the hooks
        // For now, just silently load - the state restoration needs proper implementation
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statePersistence?.key]); // Only run when the persistence key changes

  useEffect(() => {
    if (statePersistence && statePersistence.fields.length > 0) {
      const currentState = {
        pagination: pagination.pagination,
        sorting: sorting.sorting,
        filters: filterManager.filters,
        columnVisibility: columnVisibility.visibility,
        columnWidths: columnResize.columnWidths,
        columnOrder: columnReorderHook.columnOrder,
        selectedRows: selection.selected,
      };

      statePersistenceHook.saveState(currentState);
    }
  }, [
    statePersistence?.key, // Only depend on the key, not the entire config
    pagination.pagination,
    sorting.sorting,
    filterManager.filters,
    columnVisibility.visibility,
    columnResize.columnWidths,
    columnReorderHook.columnOrder,
    selection.selected,
  ]);

  return {
    // Data and rows
    rows,
    actualRows,
    paginatedRows,
    getRowId,

    // Columns
    columns,
    visibleColumns: columnVisibility.visibleColumns,
    orderedColumns,

    // Feature hooks
    selection,
    expansion,
    columnVisibility,
    columnResize,
    columnReorder: columnReorderHook,
    keyboardNavigation: keyboardNavigation?.enabled
      ? keyboardNavigationHook
      : undefined,
    inlineEdit: inlineEdit ? inlineEditHook : undefined,
    filtering: {
      filters: filterManager.filters.map((filter) => ({
        field: filter.field,
        operator: String(filter.operator),
        value: filter.value,
      })),
      addFilter: (filter: {
        field: string;
        operator: string;
        value: unknown;
      }) =>
        filterManager.addFilter({
          field: filter.field,
          operator: filter.operator as FilterOperator,
          value: filter.value,
        }),
      removeFilter: filterManager.removeFilter,
      clearFilters: filterManager.clearFilters,
      hasActiveFilters: filterManager.hasActiveFilters,
    },
    serverData: serverSide
      ? {
          data: serverDataManager.data,
          total: serverDataManager.total,
          isLoading: serverDataManager.isLoading,
          error: serverDataManager.error,
          queryParams: {
            search: serverDataManager.queryParams.search || '',
            filters: (serverDataManager.queryParams.filters || []).map(
              (filter) => ({
                field: filter.field,
                operator: String(filter.operator),
                value: filter.value,
              }),
            ),
            sorting: serverDataManager.queryParams.sorting,
            pagination: serverDataManager.queryParams.pagination,
          },
          setSearch: serverDataManager.setSearch,
          setFilters: (
            filters: Array<{ field: string; operator: string; value: unknown }>,
          ) =>
            serverDataManager.setFilters(
              filters.map((filter) => ({
                field: filter.field,
                operator: filter.operator as FilterOperator,
                value: filter.value,
              })),
            ),
          setSorting: serverDataManager.setSorting,
          setPagination: serverDataManager.setPagination,
        }
      : undefined,

    // Search
    searchValue: serverSide
      ? serverDataManager?.queryParams.search || ''
      : searchValue,
    setSearchValue: handleSearch,

    // States
    isLoading: tableState.shouldShowLoading,
    error: tableState.errorToDisplay,
    isServerSide: serverSide,

    // Configuration flags
    showHeader,
    showFilters: showFilters && filterConfigs.length > 0,
    showBulkActions: bulkActions.length > 0,
    dense,
    stickyHeader,
    filterConfigs,

    // Event handlers
    onSearch,
    onSort: handleSort,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
  };
}
